import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingApi } from '../api/bookingApi';
import {
	Box, Container, Paper, TextField, InputAdornment, IconButton, Typography, Grid, Button, Chip, Divider, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel, Card, CardContent, CardActions, Avatar
} from '@mui/material';
import { Search, Close, Add, Remove, Info, ArrowBack } from '@mui/icons-material';

const formatDate = (d) => d.toISOString().slice(0,10);

const useDebounced = (value, ms=350) => {
	const [v, setV] = useState(value);
	useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
	return v;
};

const splitAmenities = (amenities) => {
	if (!amenities) return [];
	return amenities.split(',').map(a => a.trim()).filter(Boolean);
};

export default function BookingPanel() {
	const navigate = useNavigate();
	const today = useMemo(() => new Date(), []);
	const tomorrow = useMemo(() => { const d=new Date(); d.setDate(d.getDate()+1); return d; }, []);

	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounced(search, 400);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchResults, setSearchResults] = useState([]);
	const [selectedHotel, setSelectedHotel] = useState(null);
	const [checkIn, setCheckIn] = useState(formatDate(today));
	const [checkOut, setCheckOut] = useState(formatDate(tomorrow));
	const [guests, setGuests] = useState(2);
	const [hotelDetails, setHotelDetails] = useState(null);
	const [roomSelections, setRoomSelections] = useState([]); // [{room_type_id, guests}]
	const [priceSummary, setPriceSummary] = useState(null);
	const [amenitiesOpen, setAmenitiesOpen] = useState(false);
	const [amenitiesList, setAmenitiesList] = useState([]);
	const [bookingConfirmOpen, setBookingConfirmOpen] = useState(false);
	const [bookingForm, setBookingForm] = useState({
		name: '',
		email: '',
		phone: ''
	});
	const [isBooking, setIsBooking] = useState(false);
	const [updatedPricingMap, setUpdatedPricingMap] = useState({});

	// SEARCH: call API as user types (i -> in -> ind ...)
	useEffect(() => {
		if (!debouncedSearch) { setSearchResults([]); return; }
		(async () => {
			try {
				const data = await bookingApi.searchInstantHotel(debouncedSearch);
				if (data?.status && Array.isArray(data.hotels)) {
					setSearchResults(data.hotels);
					setSearchOpen(true);
				}
			} catch (_) { /* ignore */ }
		})();
	}, [debouncedSearch]);

	// Load hotel pricing when hotel/params change
	useEffect(() => {
		if (!selectedHotel) return;
		(async () => {
			try {
				const data = await bookingApi.getHotelDetailsWithPricing({
					hotelId: selectedHotel.hotel_id,
					guests,
					checkIn,
					checkOut,
				});
				if (data?.status) {
					setHotelDetails(data);
					// Do not auto-select any rooms - let user choose manually
					setRoomSelections([]);
					setPriceSummary({
						total_actual_amount: data.total_actual_amount,
						total_discounted_price: data.total_discounted_price,
						total_gst_amount: data.total_gst_amount,
						final_price: data.final_price,
					});

					// Get updated pricing for all room types to show correct prices from start
					const allRoomSelections = data.room_details?.map(room => ({
						room_type_id: String(room.roomtype_id),
						guests: 1
					})) || [];
					
					if (allRoomSelections.length > 0) {
						try {
							const payload = {
								hotel_id: String(selectedHotel.hotel_id),
								check_in: checkIn,
								check_out: checkOut,
								room_selection: allRoomSelections,
							};
							const updatedPricing = await bookingApi.getUpdatedPrice(payload);
							if (updatedPricing?.status) {
								const map = {};
								(updatedPricing.room_details || []).forEach(rd => { 
									map[String(rd.roomtype_id)] = rd; 
								});
								setUpdatedPricingMap(map);
							}
						} catch (_) { /* ignore pricing update errors */ }
					} else {
						setUpdatedPricingMap({});
					}
				}
			} catch (_) { /* ignore */ }
		})();
	}, [selectedHotel, guests, checkIn, checkOut]);

	// Update price when roomSelections change
	useEffect(() => {
		if (!selectedHotel || roomSelections.length === 0) return;
		(async () => {
			try {
				const payload = {
					hotel_id: String(selectedHotel.hotel_id),
					check_in: checkIn,
					check_out: checkOut,
					room_selection: roomSelections.map(r => ({ room_type_id: String(r.room_type_id), guests: Number(r.guests) })),
				};
				const upd = await bookingApi.getUpdatedPrice(payload);
				if (upd?.status) {
					setPriceSummary(upd);
					const map = {};
					(upd.room_details || []).forEach(rd => { map[String(rd.roomtype_id)] = rd; });
					setUpdatedPricingMap(map);
				}
			} catch (_) { /* ignore */ }
		})();
	}, [roomSelections, checkIn, checkOut]);

	const addRoom = (roomTypeId) => {
		// Check if we can add more rooms of this type
		const roomType = hotelDetails?.room_details?.find(r => String(r.roomtype_id) === String(roomTypeId));
		if (!roomType) return;
		
		// Count how many rooms of this type are already selected
		const currentlySelected = roomSelections.filter(r => String(r.room_type_id) === String(roomTypeId)).length;
		
		// Check if we can add more rooms
		if (currentlySelected >= roomType.room_count) {
			alert(`Cannot add more rooms. Only ${roomType.room_count} ${roomType.roomtype_name?.replaceAll('_', ' ') || 'room'} available.`);
			return;
		}
		
		setRoomSelections(prev => [...prev, { room_type_id: String(roomTypeId), guests: 1 }]);
	};
	const removeRoom = (idx) => {
		setRoomSelections(prev => prev.filter((_, i) => i !== idx));
	};
	const updateGuestsForRoom = (idx, delta) => {
		setRoomSelections(prev => prev.map((r,i) => {
			if (i === idx) {
				// Find the room type details to get occupancy limit
				const roomTypeDetails = hotelDetails?.room_details?.find(rd => String(rd.roomtype_id) === String(r.room_type_id));
				const maxOccupancy = roomTypeDetails?.occupancy || 10; // fallback to 10 if not found
				
				return { ...r, guests: Math.max(1, Math.min(maxOccupancy, (r.guests||1)+delta)) };
			}
			return r;
		}));
	};

	const totalRoomsSelected = roomSelections.length;
	const totalGuestsSelected = roomSelections.reduce((sum, r) => sum + (Number(r.guests)||0), 0);

	return (
		<Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fff7f0 0%, #fff 100%)', py: 3 }}>
			<Container maxWidth="lg">
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
					<IconButton 
						onClick={() => navigate('/')} 
						sx={{ 
							color: '#FF7700',
							'&:hover': { 
								backgroundColor: 'rgba(255, 119, 0, 0.1)',
								transform: 'scale(1.05)'
							},
							transition: 'all 0.2s ease-in-out'
						}}
					>
						<ArrowBack />
					</IconButton>
					<Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>Booking Panel</Typography>
				</Box>
				<Paper sx={{ p: 2, mb: 2 }}>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} md={6}>
							<Box sx={{ position: 'relative' }}>
								<TextField 
									fullWidth 
									placeholder={selectedHotel ? `${selectedHotel.hotel_name} selected` : "Search hotels by city, name..."} 
									value={search} 
									onChange={(e)=>setSearch(e.target.value)} 
									onFocus={()=>!selectedHotel && setSearchOpen(true)} 
									InputProps={{ 
										startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>),
										endAdornment: selectedHotel && (
											<InputAdornment position="end">
												<IconButton size="small" onClick={() => { setSelectedHotel(null); setSearch(''); setSearchOpen(false); setHotelDetails(null); setRoomSelections([]); }}>
													<Close />
												</IconButton>
											</InputAdornment>
										)
									}}
								/>
								{searchOpen && searchResults.length>0 && !selectedHotel && (
									<Paper sx={{ position:'absolute', zIndex: 20, width: '100%', maxHeight: 320, overflowY:'auto', mt: 1 }}>
										{searchResults.map(h => (
											<Box key={h.hotel_id} sx={{ p: 1.5, cursor:'pointer', '&:hover':{ background:'#fff8f1' }}} onClick={()=>{ setSelectedHotel(h); setSearch(''); setSearchOpen(false); }}>
												<Typography variant="subtitle1" sx={{ color:'#FF7700', fontWeight:700 }}>{h.hotel_name}</Typography>
												<Typography variant="caption" color="text.secondary">{h.address}</Typography>
											</Box>
										))}
									</Paper>
								)}
							</Box>
						</Grid>
						<Grid item xs={6} md={2}>
							<TextField type="date" label="Check-in" fullWidth value={checkIn} onChange={(e)=>setCheckIn(e.target.value)} InputLabelProps={{ shrink: true }} />
						</Grid>
						<Grid item xs={6} md={2}>
							<TextField type="date" label="Check-out" fullWidth value={checkOut} onChange={(e)=>setCheckOut(e.target.value)} InputLabelProps={{ shrink: true }} />
						</Grid>
						<Grid item xs={12} md={2}>
							<TextField type="number" label="Guests" fullWidth value={guests} onChange={(e)=>setGuests(Math.max(1, Number(e.target.value)||1))} InputLabelProps={{ shrink: true }} />
						</Grid>
					</Grid>
				</Paper>

				{selectedHotel && hotelDetails && (
					<Box sx={{ pb: roomSelections.length > 0 ? '200px' : '20px' }}>
						<Paper sx={{ p:2, mb:2 }}>
							<Typography variant="h6" fontWeight={700}>{selectedHotel.hotel_name}</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>{selectedHotel.address}</Typography>
							{/* main image if available */}
							{hotelDetails?.hotel?.hotel_images && (
								<Box sx={{ borderRadius: 2, overflow:'hidden', border: '1px solid #eee', mb: 2, maxWidth: '100%', display: 'flex', justifyContent: 'center' }}>
									<img 
										alt="hotel" 
										src={JSON.parse(hotelDetails.hotel.hotel_images)?.main_image?.[0] ? `https://api.tabtrips.com/olivrweb/hotelapi/${JSON.parse(hotelDetails.hotel.hotel_images).main_image[0]}` : undefined} 
										style={{ 
											maxWidth: '100%', 
											height: 300, 
											objectFit: 'cover',
											borderRadius: '8px'
										}} 
									/>
								</Box>
							)}

							<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Available Room Types</Typography>
							<Grid container spacing={3}>
								{(hotelDetails.room_details||[]).map((room) => {
									const amenities = splitAmenities(room.amenities);
									const rtId = String(room.roomtype_id);
									const indicesForType = roomSelections.map((r,i)=> r.room_type_id===rtId ? i : -1).filter(i=>i!==-1);
									// Use updated pricing if available, otherwise fall back to original price
									const pricing = updatedPricingMap[rtId];
									const displayPrice = pricing?.discounted_price ?? room.discounted_price;
									const isSoldOut = room.room_count === 0;
									return (
										<Grid item xs={12} md={6} key={room.roomtype_id}>
											<Card sx={{ 
												opacity: isSoldOut ? 0.6 : 1,
												height: '100%',
												display: 'flex',
												flexDirection: 'column'
											}}>
												<CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
													<Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb: 1.5 }}>
														<Typography variant="subtitle1" fontWeight={700} sx={{ color:'#2E4057', flex: 1, mr: 1 }}>{room.roomtype_name?.replaceAll('_',' ') || `Room ${room.roomtype_id}`}</Typography>
														<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
															{!isSoldOut && (
																<Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
																	{roomSelections.filter(r => String(r.room_type_id) === String(room.roomtype_id)).length}/{room.room_count} selected
																</Typography>
															)}
															{isSoldOut && (
																<Chip label="SOLD OUT" size="small" sx={{ backgroundColor:'#f44336', color:'white', fontWeight:700 }} />
															)}
														</Box>
													</Box>
													
													<Typography variant="h6" sx={{ color:'#000', mb: 1 }}>₹{Number(displayPrice)?.toLocaleString('en-IN')}</Typography>
													<Typography variant="caption" sx={{ color:'#666', fontSize:'0.75rem', mb: 1.5 }}>Max {room.occupancy} guests per room</Typography>
													
													<Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb: 2 }}>
														{amenities.slice(0,4).map((a, idx) => (
															<Chip key={idx} size="small" label={a} sx={{ fontSize: '0.7rem' }} />
														))}
														{amenities.length>4 && (
															<Button size="small" startIcon={<Info />} onClick={()=>{ setAmenitiesList(amenities); setAmenitiesOpen(true); }} sx={{ fontSize: '0.7rem' }}>Show more</Button>
														)}
													</Box>

												{/* show selections of this type within card */}
												{indicesForType.length>0 && (
													<Box sx={{ mt: 1, borderTop:'1px dashed #eee', pt: 1, flex: 1 }}>
														{indicesForType.map((globalIdx, j) => (
															<Box key={globalIdx} sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', py: 0.5 }}>
																<Typography variant="body2">Room {j+1}</Typography>
																<Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
																	<IconButton size="small" onClick={()=>updateGuestsForRoom(globalIdx,-1)}><Remove fontSize="small" /></IconButton>
																	<Typography variant="body2">{roomSelections[globalIdx]?.guests} guest{(roomSelections[globalIdx]?.guests||1)>1?'s':''}</Typography>
																	<IconButton size="small" onClick={()=>updateGuestsForRoom(globalIdx,1)}><Add fontSize="small" /></IconButton>
																	<Button color="error" size="small" onClick={()=>removeRoom(globalIdx)} sx={{ fontSize: '0.75rem' }}>Remove</Button>
																</Box>
															</Box>
														))}
													</Box>
												)}
											</CardContent>
											<CardActions sx={{ mt: 'auto', pt: 0 }}>
												<Button 
													variant="contained" 
													onClick={()=>addRoom(room.roomtype_id)} 
													disabled={isSoldOut || roomSelections.filter(r => String(r.room_type_id) === String(room.roomtype_id)).length >= room.room_count}
													fullWidth
													sx={{ 
														background: (isSoldOut || roomSelections.filter(r => String(r.room_type_id) === String(room.roomtype_id)).length >= room.room_count) ? '#ccc' : '#FFC07A', 
														color: (isSoldOut || roomSelections.filter(r => String(r.room_type_id) === String(room.roomtype_id)).length >= room.room_count) ? '#666' : '#000', 
														'&:hover':{ background: (isSoldOut || roomSelections.filter(r => String(r.room_type_id) === String(room.roomtype_id)).length >= room.room_count) ? '#ccc' : '#FF9E42' },
														'&:disabled': { background: '#ccc', color: '#666' }
													}}
												>
													{isSoldOut ? 'Sold Out' : 
														roomSelections.filter(r => String(r.room_type_id) === String(room.roomtype_id)).length >= room.room_count ? 'Max Reached' : 'Add Room'
													}
												</Button>
											</CardActions>
										</Card>
										</Grid>
									);
								})}
							</Grid>
							
							{/* Hotel Amenities Section */}
							{hotelDetails?.amenities && (
								<Box sx={{ mt: 3 }}>
									<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: '#2E4057' }}>
										Hotel Amenities
									</Typography>
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
										{hotelDetails.amenities.split(',').map((amenity, index) => (
											<Chip 
												key={index} 
												label={amenity.trim()} 
												size="medium"
												sx={{ 
													background: '#f0f8ff', 
													color: '#2E4057',
													border: '1px solid #e3f2fd',
													'&:hover': { 
														background: '#e3f2fd',
														borderColor: '#2196f3'
													}
												}}
											/>
										))}
									</Box>
								</Box>
							)}
						</Paper>
					</Box>
				)}

				{/* Sticky Footer Summary */}
				{selectedHotel && roomSelections.length > 0 && (
					<Paper sx={{ 
						position: 'fixed', 
						bottom: 0, 
						left: 0, 
						right: 0, 
						zIndex: 1000,
						borderRadius: '12px 12px 0 0',
						boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
					}}>
						<Container maxWidth="lg">
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
								<Box sx={{ flex: 1 }}>
									<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Booking Summary</Typography>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
										{Array.from((() => { 
											const m = new Map(); 
											roomSelections.forEach((r, idx) => {
												const k = String(r.room_type_id);
												if (!m.has(k)) m.set(k, { rooms: [], totalGuests: 0 });
												const entry = m.get(k);
												entry.rooms.push({ index: idx, guests: r.guests });
												entry.totalGuests += r.guests;
											});
											return m;
										})().entries()).map(([typeId, info]) => {
											const roomDef = (hotelDetails?.room_details||[]).find(r=>String(r.roomtype_id)===typeId);
											const name = roomDef?.roomtype_name?.replaceAll('_',' ') || `Room ${typeId}`;
											return (
												<Typography key={typeId} variant="body2" sx={{ color: '#FF7700', fontWeight: 600 }}>
													{info.rooms.length} × {name} for {info.totalGuests} guest{info.totalGuests>1?'s':''}
												</Typography>
											);
										})}
									</Box>
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 3 }}>
									<Box sx={{ textAlign: 'right' }}>
										<Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
											Room Total: ₹{(priceSummary?.total_discounted_price ?? hotelDetails?.total_discounted_price ?? 0).toLocaleString('en-IN')}
										</Typography>
										<Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
											GST: ₹{(priceSummary?.total_gst_amount ?? hotelDetails?.total_gst_amount ?? 0).toLocaleString('en-IN')}
										</Typography>
										<Typography variant="h5" fontWeight={700} sx={{ color: '#FF7700', mt: 0.5 }}>
											₹{(priceSummary?.final_price ?? hotelDetails?.final_price ?? 0).toLocaleString('en-IN')}
										</Typography>
									</Box>
									<Button 
										variant="contained" 
										size="large"
										onClick={() => setBookingConfirmOpen(true)}
										sx={{ 
											background: '#FFC07A', 
											color: '#000', 
											'&:hover': { background: '#FF9E42' },
											fontWeight: 700,
											whiteSpace: 'nowrap',
											px: 3,
											py: 1.5
										}}
									>
										Book Now ({totalRoomsSelected} room{totalRoomsSelected>1?'s':''})
									</Button>
								</Box>
							</Box>
						</Container>
					</Paper>
				)}

				<Dialog open={amenitiesOpen} onClose={()=>setAmenitiesOpen(false)} maxWidth="sm" fullWidth>
					<DialogTitle>Amenities</DialogTitle>
					<DialogContent>
						<Box sx={{ display:'flex', flexWrap:'wrap', gap:1, mt:1 }}>
							{amenitiesList.map((a,i)=>(<Chip key={i} label={a} />))}
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={()=>setAmenitiesOpen(false)} startIcon={<Close />}>Close</Button>
					</DialogActions>
				</Dialog>

				{/* Booking Confirmation Dialog */}
				<Dialog open={bookingConfirmOpen} onClose={() => setBookingConfirmOpen(false)} maxWidth="sm" fullWidth>
					<DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography variant="h6" fontWeight={700}>Confirm Your Booking</Typography>
							<IconButton onClick={() => setBookingConfirmOpen(false)} size="small">
								<Close />
							</IconButton>
						</Box>
					</DialogTitle>
					
					<DialogContent sx={{ pt: 2 }}>
						{/* Hotel Information */}
						<Box sx={{ mb: 3 }}>
							<Typography variant="h6" fontWeight={700} sx={{ color: '#2E4057', mb: 0.5 }}>
								{selectedHotel?.hotel_name}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
								{selectedHotel?.address}
							</Typography>
						</Box>

						{/* Room & Guest Details with Dates */}
						<Box sx={{ mb: 3 }}>
							<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#2E4057' }}>
								Room & Guest Details
							</Typography>
							{Array.from((() => { 
								const m = new Map(); 
								roomSelections.forEach((r, idx) => {
									const k = String(r.room_type_id);
									if (!m.has(k)) m.set(k, { rooms: [], totalGuests: 0 });
									const entry = m.get(k);
									entry.rooms.push({ index: idx, guests: r.guests });
									entry.totalGuests += r.guests;
								});
								return m;
							})().entries()).map(([typeId, info]) => {
								const roomDef = (hotelDetails?.room_details||[]).find(r=>String(r.roomtype_id)===typeId);
								const name = roomDef?.roomtype_name?.replaceAll('_',' ') || `Room ${typeId}`;
								return (
									<Box key={typeId} sx={{ 
										display: 'flex', 
										justifyContent: 'space-between', 
										alignItems: 'center',
										py: 1.5,
										borderBottom: '1px solid #f0f0f0'
									}}>
										<Box>
											<Typography variant="body2" fontWeight={600} sx={{ color: '#FF7700' }}>
												{name}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{info.rooms.length} room{info.rooms.length>1?'s':''} • {info.totalGuests} guest{info.totalGuests>1?'s':''}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
											<Box sx={{ textAlign: 'center' }}>
												<Typography variant="caption" color="text.secondary">Check-in</Typography>
												<Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>{checkIn}</Typography>
											</Box>
											<Box sx={{ textAlign: 'center' }}>
												<Typography variant="caption" color="text.secondary">Check-out</Typography>
												<Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>{checkOut}</Typography>
											</Box>
											<Box sx={{ textAlign: 'center' }}>
												<Typography variant="caption" color="text.secondary">Duration</Typography>
												<Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
													{Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))} Night(s)
												</Typography>
											</Box>
										</Box>
									</Box>
								);
							})}
						</Box>

						{/* Price Details */}
						<Box sx={{ mb: 3 }}>
							<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#2E4057' }}>
								Price Details
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
									<Typography variant="caption">Room Price</Typography>
									<Typography variant="caption">₹{(priceSummary?.total_actual_amount ?? hotelDetails?.total_actual_amount ?? 0).toLocaleString('en-IN')}</Typography>
								</Box>
								{/* Show discount if available */}
								{((priceSummary?.total_actual_amount ?? hotelDetails?.total_actual_amount ?? 0) > (priceSummary?.total_discounted_price ?? hotelDetails?.total_discounted_price ?? 0)) && (
									<Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
										<Typography variant="caption">Property Discount</Typography>
										<Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
											-₹{((priceSummary?.total_actual_amount ?? hotelDetails?.total_actual_amount ?? 0) - (priceSummary?.total_discounted_price ?? hotelDetails?.total_discounted_price ?? 0)).toLocaleString('en-IN')}
										</Typography>
									</Box>
								)}
								<Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
									<Typography variant="caption">Discounted Price</Typography>
									<Typography variant="caption" fontWeight={600}>
										₹{(priceSummary?.total_discounted_price ?? hotelDetails?.total_discounted_price ?? 0).toLocaleString('en-IN')}
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
									<Typography variant="caption">Hotel GST</Typography>
									<Typography variant="caption">₹{(priceSummary?.total_gst_amount ?? hotelDetails?.total_gst_amount ?? 0).toLocaleString('en-IN')}</Typography>
								</Box>
								<Divider sx={{ my: 0.5 }} />
								<Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
									<Typography variant="body1" fontWeight={700} sx={{ color: '#FF7700' }}>Total Amount</Typography>
									<Typography variant="body1" fontWeight={700} sx={{ color: '#FF7700' }}>
										₹{(priceSummary?.final_price ?? hotelDetails?.final_price ?? 0).toLocaleString('en-IN')}
									</Typography>
								</Box>
							</Box>
						</Box>

						{/* Guest Information Form */}
						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#2E4057' }}>
								Guest Information
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<TextField
										fullWidth
										size="small"
										label="Full Name *"
										value={bookingForm.name}
										onChange={(e) => setBookingForm(prev => ({ ...prev, name: e.target.value }))}
										required
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										fullWidth
										size="small"
										label="Email"
										type="email"
										value={bookingForm.email}
										onChange={(e) => setBookingForm(prev => ({ ...prev, email: e.target.value }))}
										helperText="Either email or phone required"
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										fullWidth
										size="small"
										label="Phone Number"
										type="tel"
										value={bookingForm.phone}
										onChange={(e) => setBookingForm(prev => ({ ...prev, phone: e.target.value }))}
										helperText="Either email or phone required"
									/>
								</Grid>
							</Grid>
						</Box>
					</DialogContent>

					<DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Typography variant="h6" fontWeight={700} sx={{ color: '#FF7700' }}>
								Total: ₹{(priceSummary?.final_price ?? hotelDetails?.final_price ?? 0).toLocaleString('en-IN')}
							</Typography>
						</Box>
						<Box sx={{ display: 'flex', gap: 2 }}>
							<Button 
								onClick={() => setBookingConfirmOpen(false)} 
								sx={{ color: '#666' }}
							>
								Cancel
							</Button>
							<Button 
								variant="contained"
								disabled={isBooking}
								onClick={async () => {
									// Validate form
									if (!bookingForm.name.trim()) {
										alert('Name is required');
										return;
									}
									if (!bookingForm.email.trim() && !bookingForm.phone.trim()) {
										alert('Either email or phone number is required');
										return;
									}

									setIsBooking(true);
									try {
										// Prepare room selection payload
										const roomSelectionMap = new Map();
										roomSelections.forEach(room => {
											const key = String(room.room_type_id);
											if (!roomSelectionMap.has(key)) {
												roomSelectionMap.set(key, {
													room_type_id: key,
													room_count: 0,
													guests: 0
												});
											}
											const entry = roomSelectionMap.get(key);
											entry.room_count += 1;
											entry.guests += room.guests;
										});

										const roomSelection = Array.from(roomSelectionMap.values());

										// Prepare booking payload
										const bookingPayload = {
											hotel_id: String(selectedHotel.hotel_id),
											guest_name: bookingForm.name.trim(),
											email: bookingForm.email.trim() || '',
											// user_id: "134", // Static user ID as per your requirement? - COMMENTED OUT
											phone: bookingForm.phone.trim() || '',
											check_in: checkIn,
											check_out: checkOut,
											room_selection: roomSelection,
											total_amount: priceSummary?.total_actual_amount ?? hotelDetails?.total_actual_amount ?? 0,
											guests: totalGuestsSelected,
											discounted_amount: priceSummary?.total_discounted_price ?? hotelDetails?.total_discounted_price ?? 0,
											gst_amount: priceSummary?.total_gst_amount ?? hotelDetails?.total_gst_amount ?? 0,
											final_amount_paid: priceSummary?.final_price ?? hotelDetails?.final_price ?? 0
											// token: "2Pp7NsraFzuEcHnJwKOef6yQi1YqLbhX7M653TRZDgC8UGAIvo" // Static token as provided - COMMENTED OUT
										};

										// Call the booking API
										const response = await bookingApi.payAtHotelBooking(bookingPayload);
										
										if (response.status) {
											alert('Booking confirmed successfully!');
											setBookingConfirmOpen(false);
											// Reset form
											setBookingForm({ name: '', email: '', phone: '' });
											// Clear room selections
											setRoomSelections([]);
										} else {
											alert(`Booking failed: ${response.message || 'Unknown error'}`);
										}
									} catch (error) {
										console.error('Booking error:', error);
										alert(`Booking failed: ${error.message}`);
									} finally {
										setIsBooking(false);
									}
								}}
								sx={{ 
									background: '#FFC07A', 
									color: '#000', 
									'&:hover': { background: '#FF9E42' },
									fontWeight: 700,
									px: 3,
									py: 1
								}}
							>
								{isBooking ? 'Processing...' : 'Confirm Booking'}
							</Button>
						</Box>
					</DialogActions>
				</Dialog>
			</Container>
		</Box>
	);
}
